import React, { useEffect, useRef, useState } from 'react';
import { Leaf, Menu, Search as SearchIcon, User, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ACCOUNT_LINKS, HOME_SECTIONS, PAGE_LINKS, scrollToHash } from '../config/navigation';
import { useAuth } from '../context/AuthContext';
import CartBadge from './CartBadge';
import WishlistBadge from './WishlistBadge';
import NavSearch from './NavSearch';
import { btnPrimary, btnSm, focusRing } from '../styles/ui';

const Navbar = () => {
  const [logoError, setLogoError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { authStatus, logout } = useAuth();
  const onHome = pathname === '/';

  const handleLogoError = () => {
    setLogoError(true);
  };

  const closeMenu = () => setMenuOpen(false);

  // Escape closes the mobile menu, so keyboard users are never trapped in it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // Same Escape-to-close pattern as the mobile menu, applied to the account dropdown.
  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setAccountMenuOpen(false);
    };
    const onPointerDown = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [accountMenuOpen]);

  // Same Escape-to-close pattern, applied to the mobile search bar.
  useEffect(() => {
    if (!mobileSearchOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setMobileSearchOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileSearchOpen]);

  const handleLogout = async () => {
    setAccountMenuOpen(false);
    closeMenu();
    await logout();
    navigate('/');
  };

  // Section links are real anchors to `/#section`. On the homepage we intercept
  // and scroll smoothly (the existing behaviour). On any other route we let the
  // router navigate to `/#section`, and HomePage scrolls once it has mounted —
  // so we never hunt for homepage section ids from another page.
  const handleSectionClick = (event, hash) => {
    closeMenu();
    if (onHome) {
      event.preventDefault();
      scrollToHash(hash);
    }
  };

  const handleLogoClick = (event) => {
    closeMenu();
    if (onHome) {
      event.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const sectionLinkClass = `relative whitespace-nowrap text-gray-700 hover:text-emerald-700 font-medium transition-colors after:content-[''] after:absolute after:left-0 after:-bottom-1 after:h-0.5 after:w-0 after:bg-gradient-to-r after:from-emerald-600 after:to-accent-500 after:transition-all after:duration-300 hover:after:w-full ${focusRing}`;

  return (
    <div className="fixed w-full z-50 top-0 px-3 sm:px-4 pt-3">
      <nav className="glass max-w-7xl mx-auto rounded-2xl shadow-glass">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-[4.5rem] items-center gap-3">
          {/* Logo Area — shrink-0 so flexbox never crushes the brand to zero
              width when the row is tight (it did, below 1280px). */}
          <div className="flex items-center shrink-0" data-testid="navbar-logo-container">
            <Link
              to="/"
              onClick={handleLogoClick}
              className={`flex items-center ${focusRing}`}
              aria-label="Fresh & Homely — go to home page"
            >
              {!logoError ? (
                <img
                  src="/fresh-homely-logo.png"
                  alt="Fresh & Homely"
                  className="h-10 sm:h-12 xl:h-14 w-auto object-contain"
                  onError={handleLogoError}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gradient-primary rounded-xl flex items-center justify-center text-white shadow-glow-teal">
                    <Leaf size={24} aria-hidden="true" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xl font-bold text-emerald-900 tracking-tight leading-none uppercase">Fresh &</span>
                    <span className="text-sm font-medium text-emerald-600 tracking-widest uppercase">Homely</span>
                  </div>
                </div>
              )}
            </Link>
          </div>

          {/* Search takes the leftover room and is the only flexible item in
              the row, so it absorbs shrinkage instead of the nav links. */}
          <div className="hidden md:flex flex-1 min-w-0 justify-center px-2 lg:px-4">
            <NavSearch variant="desktop" />
          </div>

          {/* The full link row needs ~960px alongside the logo, actions and a
              usable search field, so it appears only from xl (1280px). Below
              that the same destinations stay reachable through the menu button
              — at md–lg they previously rendered but spilled off-screen. */}
          <div className="hidden xl:flex xl:gap-5 2xl:gap-8 shrink-0">
            {HOME_SECTIONS.map((item) => (
              <Link
                key={item.hash}
                to={`/${item.hash}`}
                onClick={(e) => handleSectionClick(e, item.hash)}
                className={sectionLinkClass}
              >
                {item.label}
              </Link>
            ))}
            {PAGE_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                aria-current={pathname === item.to ? 'page' : undefined}
                className={`whitespace-nowrap font-medium transition-colors ${focusRing} ${
                  pathname === item.to
                    ? 'text-emerald-700 font-semibold'
                    : 'text-gray-700 hover:text-emerald-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setMobileSearchOpen((open) => !open)}
              className={`md:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl text-emerald-900 hover:bg-emerald-50 transition-colors ${focusRing}`}
              aria-label={mobileSearchOpen ? 'Close search' : 'Open search'}
              aria-expanded={mobileSearchOpen}
              aria-controls="mobile-search-bar"
            >
              {mobileSearchOpen ? <X size={22} aria-hidden="true" /> : <SearchIcon size={22} aria-hidden="true" />}
            </button>

              <WishlistBadge />
              <CartBadge />

            {authStatus === 'authenticated' ? (
              // Ref spans the trigger *and* the dropdown so the outside-click
              // handler treats both as "inside": on the trigger it would
              // otherwise close and immediately reopen, and on a menu item it
              // would unmount the link before its click handler ran. Scoped to
              // this wrapper only, so the rest of the navbar still counts as
              // outside.
              <div ref={accountMenuRef} className="relative hidden xl:block">
                <button
                  type="button"
                  onClick={() => setAccountMenuOpen((open) => !open)}
                  aria-label="Account menu"
                  aria-expanded={accountMenuOpen}
                  aria-controls="account-menu"
                  className={`inline-flex items-center justify-center w-11 h-11 rounded-xl text-emerald-900 hover:bg-emerald-50 transition-colors ${focusRing}`}
                >
                  <User size={22} aria-hidden="true" />
                </button>
                {accountMenuOpen && (
                  <div
                    id="account-menu"
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl border border-emerald-100 shadow-lg py-2"
                  >
                    {ACCOUNT_LINKS.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setAccountMenuOpen(false)}
                        className={`block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 ${focusRing}`}
                      >
                        {item.label}
                      </Link>
                    ))}
                    <button
                      type="button"
                      onClick={handleLogout}
                      className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 ${focusRing}`}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className={`hidden xl:inline-block whitespace-nowrap text-gray-700 hover:text-emerald-700 font-medium transition-colors px-2 ${focusRing}`}
              >
                Login
              </Link>
            )}

            {/* Below 360px the bar cannot hold five controls without pushing
                one off-screen; the CTA's destination stays reachable as
                "Contact" in the menu. 360px is where it actually stops
                fitting, hence the arbitrary-value breakpoint. */}
            <Link
              to="/#contact"
              onClick={(e) => handleSectionClick(e, '#contact')}
              className={`hidden min-[360px]:inline-flex ${btnPrimary} ${btnSm} px-3 sm:h-11 sm:px-6`}
            >
              Visit Store
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              className={`xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-xl text-emerald-900 hover:bg-emerald-50 transition-colors ${focusRing}`}
              aria-label={menuOpen ? 'Close main menu' : 'Open main menu'}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              {menuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>
          </div>
        </div>

          {mobileSearchOpen && (
            <div id="mobile-search-bar" className="md:hidden pb-4 animate-fade-in" style={{ animationDuration: '200ms' }}>
              <NavSearch variant="mobile" onRequestClose={() => setMobileSearchOpen(false)} />
            </div>
          )}
        </div>

      {menuOpen && (
        <div
          id="mobile-menu"
          className="xl:hidden border-t border-white/40 bg-white/90 backdrop-blur-xl rounded-b-2xl shadow-glass"
        >
          <div className="px-4 sm:px-6 py-4 flex flex-col">
            {HOME_SECTIONS.map((item) => (
              <Link
                key={item.hash}
                to={`/${item.hash}`}
                onClick={(e) => handleSectionClick(e, item.hash)}
                className={`py-3 text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
              >
                {item.label}
              </Link>
            ))}
            {PAGE_LINKS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                aria-current={pathname === item.to ? 'page' : undefined}
                className={`py-3 font-medium transition-colors ${focusRing} ${
                  pathname === item.to
                    ? 'text-emerald-700 font-semibold'
                    : 'text-gray-700 hover:text-emerald-700'
                }`}
              >
                {item.label}
              </Link>
            ))}
            {authStatus === 'authenticated' ? (
              <>
                {ACCOUNT_LINKS.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={closeMenu}
                    className={`py-3 text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <button
                  type="button"
                  onClick={handleLogout}
                  className={`py-3 text-left text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
                >
                  Log out
                </button>
              </>
            ) : (
              <Link
                to="/login"
                onClick={closeMenu}
                className={`py-3 text-gray-700 hover:text-emerald-700 font-medium transition-colors ${focusRing}`}
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}
      </nav>
    </div>
  );
};

export default Navbar;
